import React, { useMemo } from 'react';
import { Phone } from 'lucide-react';

/**
 * PhoneInputCI - Champ telephone standardise pour la Cote d'Ivoire.
 *
 * Usage:
 *   <PhoneInputCI value={phone} onChange={setPhone} required />
 *
 * Stockage: valeur au format international brut "+225XXXXXXXXXX" (10 chiffres apres indicatif)
 * Affichage: "+225  07 07 07 07 07" (groupes de 2)
 *
 * - Force inputMode=tel et autoComplete=tel pour le bon clavier mobile
 * - Accepte la saisie avec ou sans +225, avec ou sans espaces
 * - Affiche une croix de validation quand le format est correct
 */

const CI_PREFIX = '+225';

// Nettoie la valeur: garde uniquement les chiffres apres suppression du prefixe
const cleanDigits = (raw = '') => {
  if (!raw) return '';
  let s = String(raw).replace(/[\s\-().]/g, '');
  if (s.startsWith('+225')) s = s.slice(4);
  else if (s.startsWith('00225')) s = s.slice(5);
  else if (s.startsWith('225') && s.length > 10) s = s.slice(3);
  return s.replace(/\D/g, '').slice(0, 10);
};

// Format affichage: "07 07 07 07 07"
const formatDisplay = (digits = '') => {
  if (!digits) return '';
  return digits.match(/.{1,2}/g)?.join(' ') || digits;
};

// Format stockage: "+2250707070707"
const toStorage = (digits = '') => (digits ? `${CI_PREFIX}${digits}` : '');

const isValid = (digits) => digits.length === 10;

export const PhoneInputCI = React.forwardRef(({
  value = '',
  onChange,
  placeholder = '07 07 07 07 07',
  disabled = false,
  required = false,
  className = '',
  'data-testid': testid,
  id,
  autoFocus,
}, ref) => {
  const digits = useMemo(() => cleanDigits(value), [value]);
  const display = formatDisplay(digits);
  const valid = isValid(digits);

  const handleChange = (e) => {
    const next = cleanDigits(e.target.value);
    onChange?.(toStorage(next));
  };

  return (
    <div className={`flex items-stretch rounded-md border border-input bg-transparent shadow-sm transition-colors focus-within:ring-1 focus-within:ring-ring ${valid ? 'border-emerald-500/60' : ''} ${className}`}>
      <div className="flex items-center gap-1.5 px-3 bg-slate-50 border-r border-input text-sm font-semibold text-slate-700 select-none rounded-l-md">
        <Phone className="w-3.5 h-3.5 text-slate-500" />
        <span>{CI_PREFIX}</span>
      </div>
      <input
        ref={ref}
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        pattern="[0-9\s]*"
        maxLength={14}  /* 10 chiffres + 4 espaces */
        value={display}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        autoFocus={autoFocus}
        data-testid={testid || 'phone-input-ci'}
        className="flex-1 px-3 py-1 h-9 text-base md:text-sm bg-transparent rounded-r-md focus:outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
      />
      {valid && (
        <span className="flex items-center px-2 text-emerald-600 text-xs font-bold" aria-hidden>✓</span>
      )}
    </div>
  );
});

PhoneInputCI.displayName = 'PhoneInputCI';

// Helpers exportes pour validation/affichage cote caller
export const normalizeCIPhone = (raw) => toStorage(cleanDigits(raw));
export const isValidCIPhone = (raw) => isValid(cleanDigits(raw));
export const formatCIPhone = (raw) => {
  const d = cleanDigits(raw);
  return d ? `${CI_PREFIX} ${formatDisplay(d)}` : '';
};

export default PhoneInputCI;
